import axios from 'axios'

import { httpHelper } from '../utils'
import store from '@/store'
import jwtDecode from 'jwt-decode'

const foreignTokenUrl = '/foreign-token/'

export const userService = {
    login,
    logout,
    refreshAccessToken,
    fetchUserInfo,
    updateUserInfo,
    fetchUserToken,
    fetchUserAlertSettings,
    identifyBackendFromJWT: identifyBackendFromJWT,
    fetchIdps: fetchIdps,
}

function identifyBackendFromJWT(access) {
    const backends = process.env.VUE_APP_API_URL.split(',')
    const jwt_urls = process.env.VUE_APP_JWT_API_URL.split(',')
    const websocket_urls = process.env.VUE_APP_WEBSOCKET_URL.split(',')

    // if only 1 backend is configured, don't look further
    if (backends.length === 1) {
        const backend_dict = {
            api_url: backends[0],
            jwt_url: jwt_urls[0],
            websocket_url: websocket_urls[0],
        }
        localStorage.setItem('backend', JSON.stringify(backend_dict))
        return backend_dict
    }

    const backend_site = jwtDecode(access).backend
    if (!backend_site) {
        return
    }

    for (let i = 0; i < backends.length; i++) {
        if (backends[i].startsWith(backend_site)) {
            const backend_dict = {
                api_url: backends[i],
                jwt_url: jwt_urls[i],
                websocket_url: websocket_urls[i],
            }
            localStorage.setItem('backend', JSON.stringify(backend_dict))
            return backend_dict
        }
    }
}

async function fetchIdps() {
    const responses = await Promise.allSettled(
        process.env.VUE_APP_API_URL.split(',').map(base => {
            const url = new URL('v1/idps/', base)
            return axios.get(url.toString())
        })
    )
    return responses
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => r.value.data?.results || [])
}

function login(username, password) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }

    const jwt_urls = process.env.VUE_APP_JWT_API_URL.split(',')

    const requests = jwt_urls.map(url =>
        axios.post(
            url,
            JSON.stringify({
                username,
                password,
            }),
            requestOptions
        )
    )

    return Promise.any(requests)
        .then(response => {
            const data = response.data
            // login successful if there's a jwt token in the response
            if (data.access) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('JWT', JSON.stringify(data))
            }

            return data
        })
        .catch(error => {
            // Promise.any rejects with an AggregateError if ALL requests fail
            const errors = error.errors || [error]

            const all401 =
                errors.length > 0 &&
                errors.every(err => err.response?.status === 401)

            if (all401) {
                // Logout if all the failures were 401s
                logout()
            }
            return Promise.reject(error)
        })
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('JWT')
    localStorage.removeItem('JWT-original')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('userInfo-original')
    localStorage.removeItem('backend')
}

function refreshAccessToken(refreshToken) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }

    return axios
        .post(
            store.state.auth.backend.jwt_url + 'refresh/',
            JSON.stringify({ refresh: refreshToken }),
            requestOptions
        )
        .then(response => {
            const data = response.data
            // login successful if there's a jwt token in the response
            if (data.access) {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('JWT', JSON.stringify(data))
            }

            return data
        })
        .catch(error => {
            if (error.response) {
                if (error.response.status === 401) {
                    logout()
                }
                return Promise.reject()
            }
        })
}

async function fetchUserInfo(id) {
    const httpConfig = await httpHelper.getOriginalConfig()

    const { data } = await httpHelper.get(`/users/${id}/`, httpConfig)
    data.alertsettings = await fetchUserAlertSettings(id)

    return data
}

async function updateUserInfo(id, userInfo) {
    const httpConfig = await httpHelper.getOriginalConfig()

    const { data } = await httpHelper.patch(
        `/users/${id}/`,
        userInfo,
        httpConfig
    )

    return data
}

async function fetchUserAlertSettings(id) {
    const httpConfig = await httpHelper.getOriginalConfig()

    const { data } = await httpHelper.get(
        `/users/${id}/alertsettings/`,
        httpConfig
    )

    return data
}

async function fetchUserToken(id) {
    const httpConfig = await httpHelper.getOriginalConfig()

    const { data } = await httpHelper.post(
        foreignTokenUrl,
        { foreign_user: id },
        httpConfig,
        { apiVersion: '/' }
    )

    return data
}
